/**
 * MorningBriefModal — click "Morning Brief" tile in the Moderator Portal
 * to open this. Pulls `/api/moderator/digest?hours=24` (moderators+).
 *
 * Shows both a structured breakdown (open reports, bugs, expirations,
 * meta counts) and the raw markdown rendering so moderators know
 * exactly what will land in their inbox once Resend email delivery is
 * wired up. A "Copy markdown" button lets them paste it into Slack /
 * Discord in the meantime.
 */
import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Flag, Bug, Timer, Users, MessageSquareWarning, Copy, RefreshCw } from 'lucide-react';

const humanDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const WINDOWS = [
  { label: 'Last 24 hours', value: 24 },
  { label: 'Last 3 days',   value: 72 },
  { label: 'Last week',     value: 168 },
];

export default function MorningBriefModal({ open, onOpenChange }) {
  const { API } = useContext(AuthContext);
  const [hours,   setHours]   = useState(24);
  const [loading, setLoading] = useState(false);
  const [digest,  setDigest]  = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/moderator/digest`, { params: { hours } });
      setDigest(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load brief');
    } finally {
      setLoading(false);
    }
  };

  // load is defined above with all deps closed over; safe to call on
  // open/window changes only.
  useEffect(() => {
    if (open) load();
  }, [open, hours]); // eslint-disable-line

  const copyMarkdown = async () => {
    if (!digest?.markdown) return;
    try {
      await navigator.clipboard.writeText(digest.markdown);
      toast.success('Markdown copied — paste into Slack/Discord/Email.');
    } catch {
      window.prompt('Copy this markdown:', digest.markdown);
    }
  };

  const stat = ({ icon: Icon, label, value, tone }) => (
    <div className={`rounded-lg border p-3 ${tone || 'border-slate-200 dark:border-slate-700'}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-semibold text-slate-500">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <div className="text-2xl font-bold mt-1 text-slate-900 dark:text-white">{value}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="morning-brief-modal">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <DialogTitle>Morning Brief</DialogTitle>
              <DialogDescription>
                What the moderator team should look at today. Also the exact content that will
                auto-email to mods once Resend is enabled.
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={String(hours)} onValueChange={(v) => setHours(parseInt(v, 10))}>
                <SelectTrigger className="w-40" data-testid="morning-brief-window">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WINDOWS.map((w) => (
                    <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="morning-brief-refresh">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {!digest ? (
          <p className="text-sm text-slate-500 py-6 text-center">Loading brief…</p>
        ) : (
          <div className="space-y-5">
            {/* Stat row */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {stat({ icon: Flag,                label: 'Open reports',    value: digest.open_reports_count,    tone: digest.open_reports_count > 0 ? 'border-rose-300 bg-rose-50/40 dark:bg-rose-900/10' : '' })}
              {stat({ icon: Bug,                 label: 'New bugs',        value: digest.new_bug_reports_count, tone: digest.new_bug_reports_count > 0 ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-900/10' : '' })}
              {stat({ icon: Timer,               label: 'Expiring 24h',    value: (digest.soft_ban_expirations || []).length })}
              {stat({ icon: Users,               label: 'New signups',     value: digest.new_signups_count })}
              {stat({ icon: MessageSquareWarning,label: 'Auto-flagged',    value: digest.flagged_forum_count })}
            </div>

            {/* Open reports */}
            <div>
              <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">🚩 Open reports</h4>
              {(digest.open_reports_top || []).length === 0 ? (
                <p className="text-xs text-slate-500">Nothing open right now.</p>
              ) : (
                <div className="space-y-2">
                  {digest.open_reports_top.map((r, i) => (
                    <div key={r.id || i} className="border border-rose-200 dark:border-rose-900/50 rounded-lg p-3 bg-rose-50/40 dark:bg-rose-900/10">
                      <div className="flex justify-between items-start gap-3">
                        <p className="text-sm font-medium">
                          <span className="uppercase text-xs bg-slate-200 dark:bg-slate-700 rounded px-1.5 py-0.5 mr-2">{r.target_type}</span>
                          {r.reason || 'No reason given'}
                        </p>
                        <span className="text-xs text-slate-500 whitespace-nowrap">{humanDate(r.created_at)}</span>
                      </div>
                      {r.excerpt ? (
                        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1 line-clamp-2">
                          &ldquo;{r.excerpt}&rdquo;
                        </p>
                      ) : null}
                      <p className="text-[11px] text-slate-500 mt-1">Reporter: {r.reporter || 'anon'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* New bugs */}
            <div>
              <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">🐛 New bug reports</h4>
              {(digest.new_bug_reports_top || []).length === 0 ? (
                <p className="text-xs text-slate-500">No new bugs in this window.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {digest.new_bug_reports_top.map((b, i) => (
                    <li key={b.id || i} className="flex items-start justify-between gap-3">
                      <span>
                        <span className="text-[10px] uppercase font-semibold bg-slate-200 dark:bg-slate-700 rounded px-1.5 py-0.5 mr-2">{b.type}</span>
                        {b.title || '(untitled)'}
                        <span className="text-xs text-slate-500 ml-2">· {b.status} · {b.reporter || 'anon'}</span>
                      </span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{humanDate(b.created_at)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Expirations */}
            <div>
              <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">⏳ Soft-ban expirations in next 24h</h4>
              {(digest.soft_ban_expirations || []).length === 0 ? (
                <p className="text-xs text-slate-500">Nobody rotates back to active in the next 24h.</p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {digest.soft_ban_expirations.map((u) => (
                    <li key={u.id} className="flex justify-between items-center gap-3">
                      <span>{u.email || u.id} <span className="text-xs text-slate-500">· {u.status}</span></span>
                      <span className="text-xs text-slate-500">until {humanDate(u.read_only_until || u.suspended_until)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={copyMarkdown} disabled={!digest?.markdown} data-testid="morning-brief-copy">
            <Copy className="w-4 h-4 mr-2" /> Copy markdown
          </Button>
          <Button onClick={() => onOpenChange?.(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
